import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube, X, Star, Heart } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Footer = () => {
  const { theme } = useTheme();
  
  const handleProductClick = (productName: string) => {
    if (productName === 'ApnaEduHub') {
      window.location.href = '/apnaeduhub';
    }
    // Other products can be handled here when their pages are created
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    products: [
      { name: 'ApnaEduHub', href: '/apnaeduhub', available: true },
      { name: 'Hotel Management', href: '#', available: false },
      { name: 'E-Commerce Platform', href: '#', available: false },
      { name: 'Custom Development', href: '#contact', available: true }
    ],
    services: [
      { name: 'Software Development', href: '#contact' },
      { name: 'Mobile App Development', href: '#contact' },
      { name: 'Cloud Solutions', href: '#contact' },
      { name: 'Technical Support', href: '#contact' }
    ],
    company: [
      { name: 'About Us', href: '#about' },
      { name: 'Our Team', href: '#about' },
      { name: 'Careers', href: '#contact' },
      { name: 'Contact', href: '#contact' }
    ],
    support: [
      { name: 'Documentation', href: '#' },
      { name: 'Help Center', href: '#contact' },
      { name: 'Community', href: '#' },
      { name: 'Status', href: '#' }
    ]
  };

  const socialLinks = [
    { 
      name: 'Facebook', 
      icon: Facebook, 
      href: 'https://facebook.com/neurosparktechnologies',
      color: 'hover:text-blue-500 hover:bg-blue-500/10 hover:border-blue-500/30'
    },
    { 
      name: 'Instagram', 
      icon: Instagram, 
      href: 'https://instagram.com/neurosparktechnologies',
      color: 'hover:text-pink-500 hover:bg-pink-500/10 hover:border-pink-500/30'
    },
    { 
      name: 'YouTube', 
      icon: Youtube, 
      href: 'https://youtube.com/@neurosparktechnologies',
      color: 'hover:text-red-500 hover:bg-red-500/10 hover:border-red-500/30'
    },
    { 
      name: 'LinkedIn', 
      icon: Linkedin, 
      href: 'https://linkedin.com/company/neurosparktechnologies',
      color: 'hover:text-blue-600 hover:bg-blue-600/10 hover:border-blue-600/30'
    },
    { 
      name: 'X (Twitter)', 
      icon: X, 
      href: 'https://x.com/neurosparktechnologies',
      color: 'hover:text-white hover:bg-gray-600/10 hover:border-gray-600/30'
    }
  ];

  return (
    <footer id="footer" className={`relative pt-8 border-t ${
      theme === 'light'
        ? 'bg-gradient-to-br from-gray-50 via-white to-gray-50 border-gray-200'
        : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-white/10'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute top-0 rounded-full left-20 w-80 h-80 blur-3xl ${
          theme === 'light' ? 'bg-blue-500/10' : 'bg-blue-500/5'
        }`}></div>
        <div className={`absolute bottom-0 rounded-full right-20 w-96 h-96 blur-3xl ${
          theme === 'light' ? 'bg-purple-500/10' : 'bg-purple-500/5'
        }`}></div>
      </div>

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            
            {/* Company Info */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center rounded-2xl">
                  <img 
                    src="/logo.png" 
                    alt="NeuroSpark Technologies" 
                    className="object-contain w-12 h-12"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div>
                  <span className={`text-2xl font-bold font-display ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>NeuroSpark</span>
                  <div className={`-mt-1 text-sm ${
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  }`}>Technologies</div>
                </div>
              </div>
              
              <p className={`mb-6 leading-relaxed ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-300'
              }`}>
                Empowering organizations through innovative technology solutions. We specialize in 
                creating comprehensive software ecosystems that drive digital transformation and 
                sustainable growth.
              </p>
              
              {/* Trust Indicators */}
              <div className={`p-4 mb-6 rounded-xl ${
                theme === 'light' 
                  ? 'bg-white border border-gray-200 shadow-sm' 
                  : 'glass'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className={`text-sm font-semibold ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>Trusted by 500+ Organizations</span>
                </div>
                <p className={`text-xs ${
                  theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  Join thousands of satisfied clients who trust our solutions
                </p>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <a
                  href="mailto:support@neurosparktechnologies.in"
                  className={`flex items-center gap-3 p-2 transition-all duration-300 rounded-lg group ${
                    theme === 'light'
                      ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-gray-300 hover:text-blue-400 hover:bg-blue-400/10'
                  }`}
                >
                  <Mail className="w-4 h-4 group-hover:animate-pulse" />
                  <span className="text-sm">support@neurosparktechnologies.in</span>
                </a>
                <a
                  href="tel:+919548667656"
                  className={`flex items-center gap-3 p-2 transition-all duration-300 rounded-lg group ${
                    theme === 'light'
                      ? 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                      : 'text-gray-300 hover:text-green-400 hover:bg-green-400/10'
                  }`}
                >
                  <Phone className="w-4 h-4 group-hover:animate-pulse" />
                  <span className="text-sm">+91 95486 67656</span>
                </a>
                <div className={`flex items-start gap-3 p-2 ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-300'
                }`}>
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">215/F, Govind Nagar, Mathura - 281003, Uttar Pradesh, India</span>
                </div>
              </div>
            </div>

            {/* Footer Links */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
                
                {/* Products */}
                <div>
                  <h4 className={`mb-6 text-lg font-bold font-display ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>Products</h4>
                  <ul className="space-y-3">
                    {footerLinks.products.map((link, index) => (
                      <li key={index}>
                        <button
                          onClick={() => link.available ? handleProductClick(link.name) : null}
                          className={`text-sm transition-colors text-left w-full ${
                            link.available 
                              ? theme === 'light'
                                ? 'text-gray-600 hover:text-blue-600 cursor-pointer'
                                : 'text-gray-400 hover:text-blue-400 cursor-pointer'
                              : theme === 'light'
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-600 cursor-not-allowed'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {link.name}
                            {!link.available && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                theme === 'light' 
                                  ? 'bg-gray-200 text-gray-500'
                                  : 'bg-gray-700 text-gray-400'
                              }`}>Soon</span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Services */}
                <div>
                  <h4 className={`mb-6 text-lg font-bold font-display ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>Services</h4>
                  <ul className="space-y-3">
                    {footerLinks.services.map((link, index) => (
                      <li key={index}>
                        <a
                          href={link.href}
                          className={`text-sm transition-colors ${
                            theme === 'light'
                              ? 'text-gray-600 hover:text-blue-600'
                              : 'text-gray-400 hover:text-blue-400'
                          }`}
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Company */}
                <div>
                  <h4 className={`mb-6 text-lg font-bold font-display ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>Company</h4>
                  <ul className="space-y-3">
                    {footerLinks.company.map((link, index) => (
                      <li key={index}>
                        <a
                          href={link.href}
                          className={`text-sm transition-colors ${
                            theme === 'light'
                              ? 'text-gray-600 hover:text-blue-600'
                              : 'text-gray-400 hover:text-blue-400'
                          }`}
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Support */}
                <div>
                  <h4 className={`mb-6 text-lg font-bold font-display ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>Support</h4>
                  <ul className="space-y-3">
                    {footerLinks.support.map((link, index) => (
                      <li key={index}>
                        <a
                          href={link.href}
                          className={`text-sm transition-colors ${
                            theme === 'light'
                              ? 'text-gray-600 hover:text-blue-600'
                              : 'text-gray-400 hover:text-blue-400'
                          }`}
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Download Section */}
        {/* 
        <div className="py-12 border-t border-white/10">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Smartphone className="w-5 h-5 text-blue-400" />
              <h4 className="text-xl font-bold text-white font-display">Download Our  Apps</h4>
            </div>
            <p className="mb-8 text-gray-400">
              Experience ApnaEduHub on the go with our mobile applications
            </p>
            
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center sm:gap-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.neurospark.apnaeduhub"
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 transition-all duration-300 border group glass border-white/10 hover:border-green-500/30 rounded-2xl hover:scale-105"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Get it on</div>
                    <div className="text-lg font-bold text-white transition-colors group-hover:text-green-400">
                      Google Play
                    </div>
                  </div>
                </div>
              </a>

              <a
                href="https://apps.apple.com/app/apnaeduhub/id123456789"
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 transition-all duration-300 border group glass border-white/10 hover:border-blue-500/30 rounded-2xl hover:scale-105"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Download on the</div>
                    <div className="text-lg font-bold text-white transition-colors group-hover:text-blue-400">
                      App Store
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
        */}

        {/* Bottom Copyright */}
        <div className={`py-6 border-t ${
          theme === 'light' ? 'border-gray-200' : 'border-white/10'
        }`}>
          <div className="flex flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left">
            <div className={`flex items-center gap-2 text-sm ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              <span>© {currentYear} NeuroSpark Technologies. All rights reserved.</span>
              <div className="hidden md:block">•</div>
              <span className="flex items-center gap-1">
                Made with <Heart className="w-4 h-4 text-red-500 fill-current" /> in India
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className={`text-sm font-medium ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>Follow Us:</span>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2 transition-all duration-300 transform border rounded-lg hover:scale-110 ${
                      theme === 'light'
                        ? 'text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        : 'text-white glass border-white/20 hover:bg-white/10'
                    }`}
                    aria-label={social.name}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;